import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, TextField, Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import BasicDialog from '../../base/BasicDialog';
import { useSnackbarAlert } from '../../../context/snackbarAlert';
import { useForm } from '../../../hooks/useForm';
import { useAsync, useAsyncFn } from '../../../hooks/useAsync';
import { isEmptyValue } from '../../../helpers/methods';
import TextFieldFormControl from '../../field/TextFieldFormControl';
import CheckboxField from '../../base/CheckboxField/checkboxField';
import useCorrespondenceAdminService from '../../../services/correspondenceAdminService';

const CreatePostArchiveDialog = (props) => {
  const { t } = useTranslation();
  const { formValue, setFormValue, onChange } = useForm({ post_arch_active: true });

  const { createPostArchives, updatePostArchivesData, getPostArchivesDetails } = useCorrespondenceAdminService();
  const createPostArchiveFn = useAsyncFn(createPostArchives);
  const updatePostArchiveFn = useAsyncFn(updatePostArchivesData);
  const snackbarAlert = useSnackbarAlert();

  const postArchiveDetailsData = useAsync(() => {
    if (props.archiveId) {
      return getPostArchivesDetails(props.archiveId);
    }
    return Promise.resolve({});
  }, [props.archiveId]);

  useEffect(() => {
    if (!postArchiveDetailsData.loading && postArchiveDetailsData.value && props.archiveId) {
      setFormValue(postArchiveDetailsData.value);
    }
  }, [props.archiveId, postArchiveDetailsData.loading]);

  const onChangeCheckbox = useCallback((e) => {
    const { name, checked } = e.target;
    setFormValue((prev) => ({ ...prev, [name]: checked }));
  }, []);

  const onSubmit = (postArchiveData) => {
    if (postArchiveData.id) {
      updatePostArchiveFn.execute(postArchiveData.id, postArchiveData)
        .then(() => {
          snackbarAlert.openSuccessSnackbarAlert(t("snackbar_alert.post_archive_updated"));
          if (props.onRefetchData) {
            props.onRefetchData();
          }
          props.onClose(true);
        })
        .catch(() => {
          snackbarAlert.openErrorSnackbarAlert(t("snackbar_alert.occurred_error_during_updating_post_archive"));
        });
    } else {
      createPostArchiveFn.execute(postArchiveData)
        .then(() => {
          snackbarAlert.openSuccessSnackbarAlert(t("snackbar_alert.post_archive_created"));
          if (props.onRefetchData) {
            props.onRefetchData();
          }
          props.onClose(true);
        })
        .catch(() => {
          snackbarAlert.openErrorSnackbarAlert(t("snackbar_alert.occurred_error_during_creating_new_post_archive"));
        });
    }
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      onSubmit(formValue);
    }
  };

  const isFormValid = () => {
    return !isEmptyValue(formValue.post_arch_name);
  };

  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign="center"
      maxWidth="md"
      title={props.archiveId ? t('dialog.correspondence_admin_panel_dialogs.edit_post_archive') : t('dialog.correspondence_admin_panel_dialogs.add_post_archive')}
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextFieldFormControl
            name="post_arch_name"
            label={t('dialog.correspondence_admin_panel_dialogs.post_archive_name')}
            value={formValue["post_arch_name"]}
            onChange={onChange}
            fullWidth
            required
          />
        </Grid>
        
        <Grid item xs={4}>
          <CheckboxField
            label={t('dialog.correspondence_admin_panel_dialogs.active')}
            name="post_arch_active"
            checked={formValue["post_arch_active"]}
            onChange={onChangeCheckbox}
          />
        </Grid>
        <Grid item xs={6}>
          <LoadingButton
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={!isFormValid()}
          >
            {props.archiveId ? t('save') : t('add')}
          </LoadingButton>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={props.onClose}
          >
            {t('cancel')}
          </Button>
        </Grid>
      </Grid>
    </BasicDialog>
  );
};

CreatePostArchiveDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  archiveId: PropTypes.string,
  onRefetchData: PropTypes.func.isRequired
};

CreatePostArchiveDialog.defaultProps = {
  archiveId: null
};

export default CreatePostArchiveDialog;
